import { Box, Button, Dropdown, Option, Popup, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BusinessArea,
  deleteDeliveryNote,
  DeliveryNote,
  generateDropdownOptions,
  generateDropdownOptionsForLanguage,
  getAllOrdersByIdIn,
  getAllReceiptsByIdIn,
  Language,
  Office,
  Order,
  Project,
  Receipt,
  useData,
} from 'shared';
import { getAllProjectsForIds } from 'shared/src/project/Project.axios';
import { useUser } from '../../components/UserContext';
import { convertDeliveryNotesIntoTableEntries } from '../../utils/deliveryNotes/DeliveryNotes.utils';
import { generateEmptyDeliveryNote } from '../../utils/order/Order.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

export const DeliveryNoteOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { axios, user } = useUser();
  const [filterValue, setFilterValue] = useState<string>('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [noteToCreate, setNoteToCreate] = useState<DeliveryNote>();

  const [noteToDelete, setNoteToDelete] = useState<string>();
  const { data: deliveryNotes, mutate } = useData('DELIVERY_NOTE_ALL_OFFICES', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomers } = useData('CUSTOMER_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedBusinessAreas } = useData('BUSINESSAREA_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerLocations } = useData('CUSTOMER_LOCATION_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerUsers } = useData('CUSTOMER_USERS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedOffices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  /**
   * Load the neccessary orders, projects and receipts for the deliveryNotes
   */
  useEffect(() => {
    if (deliveryNotes.length === 0) return;
    const projectIds: string[] = deliveryNotes
      .filter((note) => !!note.projectId)
      .map((note) => note.projectId!);
    const orderIds: string[] = deliveryNotes
      .filter((note) => !!note.orderId)
      .map((note) => note.orderId!);
    const receiptIds: string[] = deliveryNotes
      .filter((note) => !!note.invoiceId)
      .map((note) => note.invoiceId!);

    Promise.all([
      getAllOrdersByIdIn(axios, orderIds),
      getAllProjectsForIds(axios, projectIds),
      getAllReceiptsByIdIn(axios, receiptIds),
    ]).then(([loadedOrders, loadedProjects, loadedReceipts]) => {
      setOrders(loadedOrders);
      setProjects(loadedProjects);
      setReceipts(loadedReceipts);
    });
  }, [axios, deliveryNotes]);

  /**
   * Holds the rows to display the deliveryNotes
   */
  const rows: TableRow[] = useMemo((): TableRow[] => {
    return convertDeliveryNotesIntoTableEntries(
      deliveryNotes,
      orders,
      projects,
      receipts,
      loadedCustomerLocations,
      loadedCustomers,
      navigate,
      setNoteToDelete,
    );
  }, [
    deliveryNotes,
    loadedCustomerLocations,
    loadedCustomers,
    navigate,
    orders,
    projects,
    receipts,
  ]);

  /**
   * Util method to delete a deliveryNote
   */
  const handleDeleteDelivery = (): void => {
    if (!noteToDelete) return;
    deleteDeliveryNote(axios, noteToDelete).then(() => {
      setNoteToDelete(undefined);
      mutate();
    });
  };

  /**
   * Holds the options for businessAreas after an office was selected
   */
  const areaOptions: Option[] = useMemo((): Option[] => {
    if (!noteToCreate?.customConfig?.officeId) return [];
    const selectedOffice: Office | undefined = loadedOffices.find(
      (office) => office.id === noteToCreate?.customConfig?.officeId,
    );
    if (!selectedOffice) return [];
    const filteredAreas: BusinessArea[] = loadedBusinessAreas.filter(
      (area) => area.companyId === selectedOffice?.companyId,
    );
    return generateDropdownOptions(filteredAreas, 'name', 'id');
  }, [loadedBusinessAreas, loadedOffices, noteToCreate?.customConfig?.officeId]);

  return (
    <>
      {noteToCreate && (
        <Popup
          title={t('pages.deliveryNote.overview.createPopup.title')}
          onClose={() => setNoteToCreate(undefined)}
          isOpen={!!noteToCreate}
          buttons={[
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => setNoteToCreate(undefined)}
            />,
            <Button
              value={t('general.buttons.add')}
              onClick={() => navigate('/delivery/edit', { state: { note: noteToCreate } })}
            />,
          ]}
        >
          <div>
            <div className="three-columns">
              <Dropdown
                label={t('pages.deliveryNote.overview.createPopup.office')}
                options={generateDropdownOptions(loadedOffices, 'name', 'id')}
                onChange={(officeId) =>
                  setNoteToCreate({
                    ...noteToCreate,
                    customConfig: {
                      customerContactId: '',
                      customerId: '',
                      customerLocationId: '',
                      officeId,
                    },
                    areaId: '',
                  })
                }
              />
              <Dropdown
                selectedOption={noteToCreate.areaId}
                label={t('pages.deliveryNote.overview.createPopup.area')}
                options={areaOptions}
                onChange={(areaId) =>
                  setNoteToCreate({
                    ...noteToCreate,
                    areaId,
                  })
                }
              />
            </div>
            <div className="three-columns">
              <Dropdown
                label={t('pages.deliveryNote.overview.createPopup.customer')}
                options={generateDropdownOptions(loadedCustomers, 'name', 'id')}
                onChange={(customerId) =>
                  setNoteToCreate({
                    ...noteToCreate,
                    customConfig: {
                      customerContactId: '',
                      customerId,
                      customerLocationId: '',
                      officeId: noteToCreate.customConfig?.officeId || '',
                    },
                  })
                }
                selectedOption={noteToCreate.customConfig?.customerId}
              />
              <Dropdown
                selectedOption={noteToCreate.customConfig?.customerLocationId}
                label={t('pages.deliveryNote.overview.createPopup.customerLocation')}
                options={generateDropdownOptions(
                  loadedCustomerLocations.filter(
                    (location) => location.customerId === noteToCreate.customConfig?.customerId,
                  ),
                  'name',
                  'id',
                )}
                onChange={(customerLocationId) =>
                  setNoteToCreate({
                    ...noteToCreate,
                    customConfig: {
                      customerId: noteToCreate.customConfig?.customerId || '',
                      customerLocationId,
                      customerContactId: '',
                      officeId: noteToCreate.customConfig?.officeId || '',
                    },
                  })
                }
              />
              <Dropdown
                label={t('pages.deliveryNote.overview.createPopup.customerUser')}
                options={generateDropdownOptions(
                  noteToCreate.customConfig?.customerLocationId
                    ? loadedCustomerUsers.filter((contact) =>
                        contact.locations.includes(noteToCreate.customConfig!.customerLocationId),
                      )
                    : [],
                  ['lastName', 'firstName'],
                  'id',
                )}
                onChange={(customerContactId) =>
                  setNoteToCreate({
                    ...noteToCreate,
                    customConfig: {
                      customerId: noteToCreate.customConfig?.customerId || '',
                      customerLocationId: noteToCreate.customConfig?.customerLocationId || '',
                      customerContactId,
                      officeId: noteToCreate.customConfig?.officeId || '',
                    },
                  })
                }
                selectedOption={noteToCreate.customConfig?.customerContactId}
              />
            </div>
            <div className="three-columns">
              <Dropdown
                label={t('pages.deliveryNote.overview.createPopup.language')}
                options={generateDropdownOptionsForLanguage()}
                onChange={(language) =>
                  setNoteToCreate({
                    ...noteToCreate,
                    language: language as Language,
                  })
                }
                selectedOption={noteToCreate.language}
              />
            </div>
          </div>
        </Popup>
      )}
      {noteToDelete && (
        <Popup
          title={t('pages.deliveryNote.overview.deletePopup.title')}
          isOpen={!!noteToDelete}
          onClose={() => setNoteToDelete(undefined)}
          buttons={[
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => setNoteToDelete(undefined)}
            />,
            <Button value={t('general.buttons.yes')} onClick={() => handleDeleteDelivery()} />,
          ]}
        >
          <p>{t('pages.deliveryNote.overview.deletePopup.content')}</p>
        </Popup>
      )}
      <TopBar onSearch={setFilterValue} title={t('pages.deliveryNote.overview.topBarHeadline')}>
        <IconComponent
          type={IconType.ADD}
          className="clickable"
          onClick={() => setNoteToCreate(generateEmptyDeliveryNote({ language: user.language }))}
        />
      </TopBar>
      <Box>
        <Table
          filterValue={filterValue}
          rows={rows}
          header={
            t('pages.deliveryNote.overview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </>
  );
};
